import {
  CREATE_PLAN_CAP_COLLISION_STATUS,
  CREATE_PLAN_TOAST,
  CREATE_RESULT_STATUS,
} from './case-plan-create.constants';
import {
  buildReentryPlanDetailsPath,
  REENTRY_PLANS_LIST_PATH,
} from '../constants/reentry-planning.constants';

/**
 * Normalizes bulk-create response items.
 * @param {object|Array|null} response
 * @returns {Array}
 */
export const getCreateCasePlanResults = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  return response?.results ?? response?.data?.results ?? [];
};

/**
 * Splits bulk-create results into created / skipped / other buckets.
 * @param {object|Array|null} response
 * @returns {{ created: object[], skipped: object[], other: object[] }}
 */
export const summarizeCreateCasePlanResults = (response) => {
  const results = getCreateCasePlanResults(response);
  const created = results.filter(
    (result) => result?.status === CREATE_RESULT_STATUS.CREATED,
  );
  const skipped = results.filter(
    (result) => result?.status === CREATE_RESULT_STATUS.SKIPPED,
  );
  const other = results.filter((result) => {
    const status = result?.status;
    if (!status) {
      return false;
    }

    return (
      status !== CREATE_RESULT_STATUS.CREATED &&
      status !== CREATE_RESULT_STATUS.SKIPPED
    );
  });

  return { created, skipped, other };
};

/**
 * Picks the first successfully created plan id from a bulk-create response.
 * @param {{ results?: Array<{ status?: string, planId?: string }> }|null} response
 * @returns {string|null}
 */
export const getFirstCreatedPlanId = (response) => {
  const results = getCreateCasePlanResults(response);
  const created = results.find(
    (result) =>
      result?.status === CREATE_RESULT_STATUS.CREATED && result?.planId,
  );
  if (created?.planId) {
    return created.planId;
  }

  const withPlanId = results.find((result) => result?.planId);
  return withPlanId?.planId ?? response?.planId ?? null;
};

const CREATE_TOAST_OUTCOME = {
  ALL_SUCCESS: 'allSuccess',
  PARTIAL: 'partial',
  ALL_FAILED: 'allFailed',
  EMPTY: 'empty',
};

/**
 * @param {number} createdCount
 * @param {number} problemCount
 * @returns {string}
 */
const resolveCreateToastOutcome = (createdCount, problemCount) => {
  if (createdCount > 0 && problemCount === 0) {
    return CREATE_TOAST_OUTCOME.ALL_SUCCESS;
  }

  if (createdCount > 0) {
    return CREATE_TOAST_OUTCOME.PARTIAL;
  }

  if (problemCount > 0) {
    return CREATE_TOAST_OUTCOME.ALL_FAILED;
  }

  return CREATE_TOAST_OUTCOME.EMPTY;
};

/**
 * @param {string} outcome
 * @param {number} createdCount
 * @param {number} problemCount
 * @returns {{ type: 'success'|'warning'|'error', message: string }}
 */
const buildToastForOutcome = (outcome, createdCount, problemCount) => {
  const builders = {
    [CREATE_TOAST_OUTCOME.ALL_SUCCESS]: () => ({
      type: 'success',
      message:
        createdCount === 1
          ? CREATE_PLAN_TOAST.SUCCESS_ONE
          : CREATE_PLAN_TOAST.SUCCESS_MANY,
    }),
    [CREATE_TOAST_OUTCOME.PARTIAL]: () => ({
      type: 'warning',
      message: CREATE_PLAN_TOAST.PARTIAL(createdCount, problemCount),
    }),
    [CREATE_TOAST_OUTCOME.ALL_FAILED]: () => ({
      type: 'error',
      message: CREATE_PLAN_TOAST.ALL_FAILED,
    }),
    [CREATE_TOAST_OUTCOME.EMPTY]: () => ({
      type: 'error',
      message: CREATE_PLAN_TOAST.REQUEST_FAILED,
    }),
  };

  return builders[outcome]();
};

/**
 * Toast copy + variant for a bulk-create response summary.
 * @param {{ created: object[], skipped: object[], other: object[] }} summary
 * @returns {{ type: 'success'|'warning'|'error', message: string }}
 */
export const buildCreateCasePlanToast = ({ created, skipped, other }) => {
  const createdCount = created?.length ?? 0;
  const problemCount = (skipped?.length ?? 0) + (other?.length ?? 0);
  const outcome = resolveCreateToastOutcome(createdCount, problemCount);
  return buildToastForOutcome(outcome, createdCount, problemCount);
};

/**
 * Toast for a failed create request (network / HTTP error).
 * Cap collision (409) gets dedicated copy per CC-14981 AC#5.
 * @param {{ status?: number }|null|undefined} error
 * @returns {{ type: 'error', message: string }}
 */
export const buildCreateCasePlanErrorToast = (error) => {
  const isCapCollision = error?.status === CREATE_PLAN_CAP_COLLISION_STATUS;
  return {
    type: 'error',
    message: isCapCollision
      ? CREATE_PLAN_TOAST.CAP_REACHED
      : CREATE_PLAN_TOAST.REQUEST_FAILED,
  };
};

/**
 * Post-create navigation path, or null to stay on the form.
 * @param {{ createdCount: number, eligibleResidentCount: number, firstPlanId: string|null }} params
 * @returns {string|null}
 */
export const resolvePostCreateNavigationPath = ({
  createdCount,
  eligibleResidentCount,
  firstPlanId,
}) => {
  if (createdCount === 0) {
    return null;
  }

  if (eligibleResidentCount > 1) {
    return REENTRY_PLANS_LIST_PATH;
  }

  if (firstPlanId) {
    return buildReentryPlanDetailsPath(firstPlanId);
  }

  return null;
};
